import React from 'react';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { onInvalidScroll } from '~/utils/FormUtil';
import { isSuccess } from '~/utils/api/api_handler';
import { VendorManagementCompanySettingForm } from './VendorManagementCompanySettingForm';
import type { VendorManagementCompanySetting } from '~/models/VendorManagementCompanySetting';
import { validationSchema } from '~/models/VendorManagementCompanySetting';
import type { CompanySettingWithItemSetting } from '~/models/CompanySetting';
import { update } from '~/utils/api/vendors/VendorManagementCompanySetting';
import { useVendorManagementCompanySettings } from '~/hooks/vendors/useVendorManagementCompanySettings';

type Props = {
  companySetting: CompanySettingWithItemSetting;
  isOpen: boolean;
  handleClose: () => void;
};

export const VendorManagementCompanySettingModal: React.FC<Props> = ({
  companySetting,
  isOpen,
  handleClose
}: Props) => {
  const { data: setting, mutate: mutateSetting } =
    useVendorManagementCompanySettings().show(companySetting.companyMasterId);
  const methods = useForm<VendorManagementCompanySetting>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  });
  React.useEffect(() => {
    if (setting) {
      methods.reset(setting);
    }
  }, [setting, methods]);
  const handleModalSubmit = React.useCallback(() => {
    methods.handleSubmit(
      async (submitValue: VendorManagementCompanySetting) => {
        const response = await update(
          companySetting.companyMasterId,
          submitValue
        );
        if (isSuccess(response)) {
          mutateSetting?.();
          handleClose();
          toast.success('更新しました');
          return;
        }
        handleClose();
        toast.error('エラーが発生しました');
      },
      (errors) => onInvalidScroll(errors)
    )();
  }, [methods, companySetting, handleClose, mutateSetting]);
  return (
    <Modalv2 isOpen={isOpen} onClose={handleClose} size="l">
      <ModalHeader title={`${companySetting.name} 設定の更新`} />
      <ModalBody>
        <FormProvider {...methods}>
          <VendorManagementCompanySettingForm />
        </FormProvider>
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button
            onClick={(): void => {
              methods.reset();
              handleClose();
            }}
            variant="secondary"
          >
            保存せず閉じる
          </Button>
        }
        rightArea={
          <Button
            disabled={methods.formState.isSubmitting}
            onClick={handleModalSubmit}
            variant="primary"
          >
            更新
          </Button>
        }
      />
    </Modalv2>
  );
};
